import React, { useState } from 'react';
import goldColors from '../themes/goldColors';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
// 17 sep 2026: contador de solicitudes de consulta sin responder, para que
// no haya que bajar hasta ADMIN para enterarse de que llegó una.
import useConsultasPendientes from '../hooks/useConsultasPendientes';
import useRevisionesPendientes from '../hooks/useRevisionesPendientes';
import { esAdmin, esSuperAdmin } from '../utils/roles';
// Iconos
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SubjectIcon from '@mui/icons-material/Subject';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MicIcon from '@mui/icons-material/Mic';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RuleIcon from '@mui/icons-material/Rule';
// 25 ago 2026 (Task #10): ícono para el link nuevo de "Categorías".
import CategoryIcon from '@mui/icons-material/Category';
import PieChartIcon from '@mui/icons-material/PieChart';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HubIcon from '@mui/icons-material/Hub';
// 17 sep 2026: la lista de emails se movió a utils/roles.js -- estaba
// copiada en cinco archivos y ya había divergido del backend. Sigue
// siendo solo cosmético: el control de acceso real vive en core/deps.py.

const SidebarWrapper = styled(Box)(({ theme }) => ({
  width: 220,
  flexShrink: 0,
  overflowY: 'auto',
  backgroundColor: theme.palette.background.sidebar,
  height: '100vh',
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'light' ? '#f1f1f1' : '#2D2D2D',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'light' ? '#c1c1c1' : '#555',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.mode === 'light' ? '#a1a1a1' : '#666',
  },
}));
const SidebarItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.3, 1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));
const SidebarButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '4px',
  padding: theme.spacing(0.3, 1),
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));
const SidebarSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));
const SidebarSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: theme.spacing(0.7, 2, 0.3),
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.2),
}));
const UserSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));
// Componente principal
const NotionSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const { pendientes: consultasPendientes } = useConsultasPendientes();
  // 21 sep 2026: movimientos importados que esperan una decisión. A
  // diferencia del de consultas, este lo ve cualquier usuario -- y pesa
  // más, porque mientras haya pendientes los totales están incompletos a
  // propósito (ver hooks/useRevisionesPendientes.js).
  const { pendientes: revisionesPendientes } = useRevisionesPendientes();
  // 25 sep 2026 (Augusto: "está mucho mejor el menú de la izquierda,
  // solo sería bueno tener la opción de collapse or expand").
  //
  // Vuelve el contraer/expandir por grupo, pero recordado: un menú que
  // se vuelve a abrir entero en cada recarga hace que contraerlo no
  // sirva de nada. Se guarda en localStorage, igual que los filtros de
  // Transacciones y la vista del Calendario.
  const [seccionesAbiertas, setSeccionesAbiertas] = useState(() => {
    const porDefecto = { comoVoy: true, miDinero: true, movimientos: true, planificacion: true };
    try {
      const guardado = localStorage.getItem('pcm_menu_secciones');
      return guardado ? { ...porDefecto, ...JSON.parse(guardado) } : porDefecto;
    } catch {
      // localStorage bloqueado o JSON corrupto: se abre todo, que es el
      // estado en que el menú siempre funciona.
      return porDefecto;
    }
  });
  const alternarSeccion = (clave) => {
    setSeccionesAbiertas((prev) => {
      const siguiente = { ...prev, [clave]: !prev[clave] };
      try {
        localStorage.setItem('pcm_menu_secciones', JSON.stringify(siguiente));
      } catch {
        // No es crítico: se pierde el recuerdo, no la función.
      }
      return siguiente;
    });
  };
  // Obtener iniciales del usuario para el avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    console.log('Datos de usuario para avatar:', user);
    // Usar displayName si existe, o combinar firstName y lastName
    const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`;
    // Si no hay ningún nombre, mostrar la primera letra del email o 'U'
    if (!displayName.trim()) {
      if (user.email) {
        return user.email.charAt(0).toUpperCase();
      }
      return 'U';
    }
    const initials = displayName.split(' ')
      .filter(part => part.length > 0) // Filtrar partes vacías
      .map(name => name.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
    console.log('Iniciales generadas:', initials);
    return initials;
  };
  // Gestionar la navegación
  const handleNavigation = (path) => {
    navigate(path);
  };
  // Verificar si la ruta actual corresponde a un elemento del menú
  const isSelected = (path) => location.pathname === path;
  return (
    <SidebarWrapper>
      {/* Sección de usuario */}
      <UserSection onClick={() => handleNavigation('/profile')}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              bgcolor: 'primary.main',
              mr: 1.5
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Typography variant="body2" fontWeight={500} noWrap>
            {user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || t('common.guest', 'Guest')}
          </Typography>
        </Box>
        <Tooltip title={t('profile.settings', 'Settings')}>
          <IconButton size="small">
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </UserSection>
      <Divider />
      {/* Sección de navegación principal */}
      <List disablePadding>
        <SidebarItem disablePadding>
          <SidebarButton
            selected={isSelected('/welcome')}
            onClick={() => {
              // Inicio = pagina de bienvenida (resumen + progreso), separada
              // del Tablero (panel financiero detallado, mas abajo en Vistas).
              // 7 sep 2026 (Augusto: "la velocidad ha bajado mucho... demora
              // al moverse de sección a sección"): todos los links de este
              // sidebar usaban window.location.href, que hace una recarga
              // COMPLETA de la página (rehace el bundle JS, remonta toda la
              // app, repite fetches de /dashboard/accounts, /onboarding/data,
              // /subscription/current, etc.) en vez de una navegación rápida
              // de React Router. Se reemplaza por handleNavigation (ya
              // definido arriba con useNavigate) en todos los links.
              handleNavigation('/welcome');
            }}
          >
            <ListItemIcon>
              <HomeIcon fontSize="small" sx={{ color: goldColors.main }} />
            </ListItemIcon>
            <ListItemText
              primary={language === 'es' ? 'Inicio' : 'Home'}
              primaryTypographyProps={{ fontSize: '0.8rem' }}
            />
          </SidebarButton>
        </SidebarItem>
      </List>
      {/* 25 sep 2026 (Augusto: "¿se puede ordenar mejor la vista, sobre
          todo del menú de la izquierda?").
          Antes había dos grupos: VISTAS (Tablero, Calendario, Informes,
          Voz) y REGISTROS con NUEVE ítems que mezclaban tres cosas
          distintas -- dónde está tu plata, qué pasó con ella, y qué
          planeas. Nueve opciones sin jerarquía se leen como una lista
          plana: hay que recorrerlas todas para encontrar una.
          Ahora los grupos siguen el recorrido real: mirar cómo voy,
          revisar dónde está mi dinero, registrar lo que pasó, planificar
          lo que viene. Y "Voz" salió de VISTAS: no es una vista, es una
          forma de ingresar datos, así que va con los movimientos. */}
      <SidebarSection>
        <SidebarSectionTitle>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {language === 'es' ? 'Cómo voy' : 'How am I doing'}
          </Typography>
          <IconButton size="small" onClick={() => alternarSeccion('comoVoy')} sx={{ p: 0.3 }}>
            {seccionesAbiertas.comoVoy
              ? <ExpandMoreIcon fontSize="small" sx={{ color: goldColors.main }} />
              : <ChevronRightIcon fontSize="small" sx={{ color: goldColors.main }} />}
          </IconButton>
        </SidebarSectionTitle>
        <Collapse in={seccionesAbiertas.comoVoy} timeout="auto" unmountOnExit>
        <List disablePadding>
            <SidebarItem disablePadding>
              <SidebarButton
                onClick={() => {
                  // Navegar al dashboard y establecer la vista como tablero/resumen
                  handleNavigation('/dashboard');
                  localStorage.setItem('dashboardView', 'summary');
                }}
              >
                <ListItemIcon>
                  <DashboardIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Tablero' : 'Dashboard'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/calendar')}
                onClick={() => {
                  // FIX: este botón solo guardaba localStorage.dashboardView
                  // y navegaba a /dashboard, pero NotionDashboard.js nunca
                  // leía ese flag — el enlace "Calendario" no hacía nada
                  // útil. Ahora navega a la página real /calendar.
                  handleNavigation('/calendar');
                }}
              >
                <ListItemIcon>
                  <CalendarMonthIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Calendario' : 'Calendar'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/reports')}
                onClick={() => {
                  handleNavigation('/reports');
                }}
              >
                <ListItemIcon>
                  <SubjectIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Informes' : 'Reports'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
        </List>
        </Collapse>
      </SidebarSection>
      <SidebarSection>
        <SidebarSectionTitle>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {language === 'es' ? 'Mi dinero' : 'My money'}
          </Typography>
          <IconButton size="small" onClick={() => alternarSeccion('miDinero')} sx={{ p: 0.3 }}>
            {seccionesAbiertas.miDinero
              ? <ExpandMoreIcon fontSize="small" sx={{ color: goldColors.main }} />
              : <ChevronRightIcon fontSize="small" sx={{ color: goldColors.main }} />}
          </IconButton>
        </SidebarSectionTitle>
        <Collapse in={seccionesAbiertas.miDinero} timeout="auto" unmountOnExit>
        <List disablePadding>
            <SidebarItem disablePadding>
              <SidebarButton
                onClick={() => {
                  handleNavigation('/accounts');
                }}
              >
                <ListItemIcon>
                  <AccountBalanceWalletIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Cuentas' : 'Accounts'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                onClick={() => {
                  handleNavigation('/debts');
                }}
              >
                <ListItemIcon>
                  <SavingsIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Deuda' : 'Debt'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                onClick={() => {
                  handleNavigation('/investments');
                }}
              >
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Inversión' : 'Investment'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
        </List>
        </Collapse>
      </SidebarSection>
      <SidebarSection>
        <SidebarSectionTitle>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {language === 'es' ? 'Movimientos' : 'Transactions'}
          </Typography>
          <IconButton size="small" onClick={() => alternarSeccion('movimientos')} sx={{ p: 0.3 }}>
            {seccionesAbiertas.movimientos
              ? <ExpandMoreIcon fontSize="small" sx={{ color: goldColors.main }} />
              : <ChevronRightIcon fontSize="small" sx={{ color: goldColors.main }} />}
          </IconButton>
        </SidebarSectionTitle>
        <Collapse in={seccionesAbiertas.movimientos} timeout="auto" unmountOnExit>
        <List disablePadding>
            <SidebarItem disablePadding>
              <SidebarButton
                onClick={() => {
                  handleNavigation('/transactions');
                }}
              >
                <ListItemIcon>
                  <PaymentsIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Transacciones' : 'Transactions'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            {revisionesPendientes > 0 && (
              <SidebarItem disablePadding>
                <SidebarButton
                  selected={isSelected('/revision')}
                  onClick={() => { handleNavigation('/revision'); }}
                >
                  <ListItemIcon>
                    <Badge color="error" variant="dot" overlap="circular">
                      <RuleIcon fontSize="small" sx={{ color: goldColors.main }} />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={language === 'es' ? 'Revisión pendiente' : 'Pending review'}
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                  />
                  <Box
                    sx={{
                      minWidth: 18, height: 18, px: 0.5, borderRadius: '9px',
                      bgcolor: 'error.main', color: '#fff', fontSize: '0.7rem',
                      fontWeight: 700, display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {revisionesPendientes}
                  </Box>
                </SidebarButton>
              </SidebarItem>
            )}
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/conciliacion')}
                onClick={() => {
                  handleNavigation('/conciliacion');
                }}
              >
                <ListItemIcon>
                  <FactCheckIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Conciliación' : 'Reconciliation'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/voice')}
                onClick={() => {
                  handleNavigation('/voice');
                }}
              >
                <ListItemIcon>
                  <MicIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Voz' : 'Voice'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            {/* 2 nov 2026: Telegram y la carpeta de Google Drive. */}
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/conexiones')}
                onClick={() => {
                  handleNavigation('/conexiones');
                }}
              >
                <ListItemIcon>
                  <HubIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Conexiones' : 'Connections'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
        </List>
        </Collapse>
      </SidebarSection>
      <SidebarSection>
        <SidebarSectionTitle>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {language === 'es' ? 'Planificación' : 'Planning'}
          </Typography>
          <IconButton size="small" onClick={() => alternarSeccion('planificacion')} sx={{ p: 0.3 }}>
            {seccionesAbiertas.planificacion
              ? <ExpandMoreIcon fontSize="small" sx={{ color: goldColors.main }} />
              : <ChevronRightIcon fontSize="small" sx={{ color: goldColors.main }} />}
          </IconButton>
        </SidebarSectionTitle>
        <Collapse in={seccionesAbiertas.planificacion} timeout="auto" unmountOnExit>
        <List disablePadding>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/budgets')}
                onClick={() => {
                  handleNavigation('/budgets');
                }}
              >
                <ListItemIcon>
                  <PieChartIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Presupuestos' : 'Budgets'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/recurring')}
                onClick={() => {
                  handleNavigation('/recurring');
                }}
              >
                <ListItemIcon>
                  <TrendingUpIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Recurrentes' : 'Recurring'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/categorias')}
                onClick={() => {
                  handleNavigation('/categorias');
                }}
              >
                <ListItemIcon>
                  <CategoryIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Categorías' : 'Categories'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
        </List>
        </Collapse>
      </SidebarSection>
      {/* Sección de Recursos: Guías/Soporte -- 12 ago 2026 (pedido de
          Augusto), se sacaron del sitio público (dropdown de
          PublicHeader.js) y se movieron acá, al menú que ve el cliente ya
          registrado. Siempre visible, sin Collapse, mismo criterio que
          REGISTROS más arriba -- es una sección chica que no justifica
          ocupar espacio con un botón de expandir/contraer.
          3 sep 2026 (pedido de Augusto: "quita la sección Blog"): esta
          sección tenía Guías/Blog/Soporte -- Blog nunca tuvo contenido
          real (BlogPage.js solo mostraba "En construcción"), así que se
          quita el link de acá y la ruta /blog en routes.js. El archivo
          BlogPage.js se deja en el repo sin usar, por si Augusto quiere
          reactivarlo más adelante con contenido real. */}
      <SidebarSection>
        <SidebarSectionTitle>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {language === 'es' ? 'Recursos' : 'Resources'}
          </Typography>
        </SidebarSectionTitle>
        <List disablePadding>
          <SidebarItem disablePadding>
            <SidebarButton
              selected={isSelected('/guides')}
              onClick={() => { handleNavigation('/guides'); }}
            >
              <ListItemIcon>
                <MenuBookIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              <ListItemText
                primary={language === 'es' ? 'Guías' : 'Guides'}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </SidebarButton>
          </SidebarItem>
          <SidebarItem disablePadding>
            <SidebarButton
              selected={isSelected('/support')}
              onClick={() => { handleNavigation('/support'); }}
            >
              <ListItemIcon>
                <SupportAgentIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              <ListItemText
                primary={language === 'es' ? 'Soporte' : 'Support'}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </SidebarButton>
          </SidebarItem>
        </List>
      </SidebarSection>
      {/* Sección de Análisis Financiero */}
      {/* Welcome section removed as requested */}
      {/* Workspaces section removed as requested */}
      {/* Panel de administración: solo visible para el email admin — ver
          ADMIN_EMAILS arriba. Muestra el costo de Anthropic por cliente
          (endpoints/admin.py). */}
      {esAdmin(user) && (
        <SidebarSection>
          <SidebarSectionTitle>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              <Badge color="error" variant="dot" invisible={!consultasPendientes} sx={{ pr: 0.5 }}>
                ADMIN
              </Badge>
            </Typography>
          </SidebarSectionTitle>
          <List disablePadding>
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/clientes')}
                onClick={() => { handleNavigation('/clientes'); }}
              >
                <ListItemIcon>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Consumo IA Docs/Clientes' : 'AI Usage/Client'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            {/* 3 sep 2026 (Augusto: "no, lo quiero como otra sección
                dentro de ADMIN"): segundo link de ADMIN -- regalar un
                plan Pro+ sin cobrar a cuentas de testing (ver
                TestersPage.js, POST /subscription/comp).
                17 sep 2026: solo para la cuenta de la empresa, no para la
                cuenta personal de Augusto (ver utils/roles.js). */}
            {esSuperAdmin(user) && (
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/testers')}
                onClick={() => { handleNavigation('/testers'); }}
              >
                <ListItemIcon>
                  <CardGiftcardIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Cuentas de testing' : 'Testing accounts'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </SidebarButton>
            </SidebarItem>
            )}
            {/* 17 sep 2026: agenda propia de consultas, que reemplaza a
                Cal.com. Acá se confirman o rechazan las solicitudes que
                llegan de la página pública y se edita el horario de
                atención (ver pages/ConsultasPage.js). */}
            <SidebarItem disablePadding>
              <SidebarButton
                selected={isSelected('/consultas')}
                onClick={() => { handleNavigation('/consultas'); }}
              >
                <ListItemIcon>
                  {/* El punto rojo acompaña al ícono para que se vea también
                      cuando la barra está angosta y el texto no entra. */}
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={!consultasPendientes}
                    overlap="circular"
                  >
                    <EventAvailableIcon fontSize="small" sx={{ color: goldColors.main }} />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={language === 'es' ? 'Consultas agendadas' : 'Scheduled consultations'}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                {consultasPendientes > 0 && (
                  <Box
                    sx={{
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: '9px',
                      bgcolor: 'error.main',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {consultasPendientes}
                  </Box>
                )}
              </SidebarButton>
            </SidebarItem>
          </List>
        </SidebarSection>
      )}
      <Divider sx={{ my: 1 }} />
    </SidebarWrapper>
  );
};
export default NotionSidebar;
